import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Eyebrow, Marquee } from "@/components/Motion";
import { SOLUTIONS, img } from "@/lib/content";

const Solutions = () => (
  <>
    <Seo
      title="Solutions — NEXREQ UAE & GCC"
      siteName="NEXREQ"
      description="Direct hire, executive search, recruitment process outsourcing and talent advisory across the UAE and GCC."
    />
    <PageHero
      eyebrow="Solutions"
      lines={["Precision Hiring", "Across the GCC."]}
      description="Four disciplines. One standard. Every NEXREQ engagement is built around measurable outcomes and senior accountability."
      image={img("photo-1560179707-f14e90ef3623", 1920)}
    />

    <section className="bg-obsidian py-24 lg:py-32" data-testid="solutions-list">
      <div className="nx-container space-y-28">
        {SOLUTIONS.map((s, i) => (
          <div
            key={s.name}
            className="grid gap-12 border-t border-white/10 pt-14 lg:grid-cols-12"
            data-testid={`solution-chapter-${i}`}
          >
            <div className="lg:col-span-4">
              <span className="font-mono text-xs tracking-[0.3em] text-emerald-500">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="mt-4 font-heading text-3xl font-light tracking-tight text-white lg:text-4xl">
                {s.name}
              </h2>
              <p className="mt-4 font-heading text-lg font-light italic text-white/40">{s.tagline}</p>
            </div>
            <div className="lg:col-span-5">
              <p className="leading-relaxed text-white/55">{s.description}</p>
              <ul className="mt-8 space-y-3">
                {s.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm text-white/70">
                    <Check size={15} className="mt-0.5 shrink-0 text-emerald-500" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
            <Reveal className="lg:col-span-3" delay={0.15}>
              <div className="border border-white/10 bg-surface p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
                  Ideal For
                </p>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  {i === 0 && "Organizations hiring critical permanent roles where quality and speed both matter."}
                  {i === 1 && "Boards and CEOs appointing C-suite, country leadership and strategic hires."}
                  {i === 2 && "High-growth organizations with sustained or project-based hiring demand."}
                  {i === 3 && "Leadership teams planning entry, expansion or workforce transformation in the GCC."}
                </p>
                <Link
                  to="/hire-talent"
                  className="mt-6 inline-flex items-center gap-2 text-sm text-emerald-400 transition-colors hover:text-emerald-300"
                  data-testid={`solution-cta-${i}`}
                >
                  Discuss this solution <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        ))}
      </div>
    </section>

    <Marquee
      className="border-y border-white/[0.08] bg-surface py-6 font-heading text-xl font-light uppercase tracking-[0.2em] text-white/20 lg:text-2xl"
      items={["Global Talent", "Regional Expertise", "Precision Hiring", "North American Standards", "GCC Execution"]}
    />

    <section className="bg-obsidian py-24" data-testid="solutions-cta">
      <div className="nx-container grid gap-10 lg:grid-cols-[2fr_1fr] lg:items-end">
        <div>
          <Reveal>
            <Eyebrow>Begin a Mandate</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
            Tell Us What You Are Hiring For.
          </h2>
        </div>
        <Reveal delay={0.2} className="lg:justify-self-end">
          <Link to="/hire-talent" className="nx-btn-primary" data-testid="solutions-hire-btn">
            Submit a Hiring Requirement <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default Solutions;
